import { useImageLoading } from "../../../helpers/ImageLoadingContext";
import { getImageSource } from "../../../helpers/getImageSource";
import { getStoryblokImageUrl } from "../../../helpers/images/getStoryblokImageUrl";
import {
  TYPICAL_VIEWPORT_WIDTHS,
  // responsiveImageHelper,
} from "../../../helpers/responsiveImageHelper";
import {
  CosmosButton,
  CosmosIconChevronRight,
  CosmosTitle,
} from "@cosmos/web-react";
import classnames from "classnames/bind";
import Link from "next/link";
import { useState } from "react";
// import Image from "next/image";
import styles from "./CategoryHeader.module.css";

const cx = classnames.bind(styles);

export const CategoryHeader = ({
  title,
  backgroundImageUrl,
  className,
  href,
  verticalAlign = "start",
  height = "dynamic",
}: {
  title: string;
  backgroundImageUrl?: string;
  className?: string;
  href?: string;
  verticalAlign?: "start" | "center";
  height: "short" | "dynamic";
}) => {
  const imageLoading = useImageLoading();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={cx("container", className)}
      data-vertical-align={verticalAlign}
      data-height={height}
    >
      <div className={cx("inner")}>
        <div className={cx("content")}>
          <CosmosTitle tag="h2" size="x-small" weight="bold" appearance="light">
            {href ? (
              <Link
                className={cx("title-link")}
                href={href}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {title}
              </Link>
            ) : (
              <span>{title}</span>
            )}
          </CosmosTitle>
          {href && (
            <CosmosButton
              className={cx("decorative-button")}
              decorative={true}
              type="button"
              kind="cover-up"
              size="medium"
              appearance="dark"
              shape="circle"
              hovered={isHovered}
            >
              <CosmosIconChevronRight slot="icon" />
            </CosmosButton>
          )}
        </div>
      </div>
      <div className={cx("inner-background")}>
        <div className={cx("gradient")} />

        {/* TODO: Design has changed, we can probably remove this gradient and background image */}
        {backgroundImageUrl &&
          (() => {
            const imageSource = getImageSource(backgroundImageUrl);

            const imageProps = {
              alt: "",
              loading: imageLoading,
            };

            switch (imageSource) {
              case "storyblok": {
                // Create responsive srcsets for height-based cropping
                const breakpoint = 650;
                const desktopWidths = TYPICAL_VIEWPORT_WIDTHS.filter(
                  (width) => width >= breakpoint,
                );
                const mobileWidths = TYPICAL_VIEWPORT_WIDTHS.filter(
                  (width) => width < breakpoint,
                );

                // Desktop srcset (650px+, 146px high)
                // For 1200px+ viewports, use half width due to two-column layout
                const desktopSrcSet = desktopWidths
                  .map((width) => {
                    const cropWidth =
                      width >= 1200 ? Math.round(width / 2) : width;
                    return `${getStoryblokImageUrl(backgroundImageUrl, {
                      width: cropWidth,
                      height: 146,
                      smart: true,
                    })} ${width}w`;
                  })
                  .join(", ");

                // Mobile srcset (under 650px, 85px high)
                const mobileSrcSet = mobileWidths
                  .map(
                    (width) =>
                      `${getStoryblokImageUrl(backgroundImageUrl, {
                        width,
                        height: 85,
                        smart: true,
                      })} ${width}w`,
                  )
                  .join(", ");

                return (
                  <picture className={cx("background")}>
                    {/* Desktop: 650px and wider, 146px high */}
                    <source
                      media="(min-width: 650px)"
                      srcSet={desktopSrcSet}
                      sizes="(min-width: 1200px) 50vw, 100vw"
                    />
                    {/* Mobile: under 650px, 85px high */}
                    <source
                      srcSet={mobileSrcSet}
                      sizes="(min-width: 480px) 480px, 100vw"
                    />
                    {/* Fallback image */}
                    <img
                      {...imageProps}
                      src={getStoryblokImageUrl(backgroundImageUrl, {
                        width: 510,
                        height: 85,
                        smart: true,
                      })}
                    />
                  </picture>
                );
              }
              default: {
                return <img {...imageProps} src={backgroundImageUrl} />;
              }
            }
          })()}
      </div>
    </div>
  );
};
